import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Table, Button, DatePicker, Space, message } from 'antd';
import { DollarOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import api from '../utils/api';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

const getRange = (preset) => {
  const today = dayjs().startOf('day');
  if (preset === 'today') return { startDate: today.format('YYYY-MM-DD'), endDate: today.format('YYYY-MM-DD') };
  if (preset === 'week') return { startDate: today.subtract(6, 'day').format('YYYY-MM-DD'), endDate: dayjs().format('YYYY-MM-DD') };
  if (preset === 'month') return { startDate: today.startOf('month').format('YYYY-MM-DD'), endDate: dayjs().format('YYYY-MM-DD') };
  if (preset === 'year') return { startDate: today.startOf('year').format('YYYY-MM-DD'), endDate: dayjs().format('YYYY-MM-DD') };
  return null;
};

const RevenueSummary = () => {
  const defaultRange = getRange('month');
  const [dateRange, setDateRange] = useState([dayjs(defaultRange.startDate), dayjs(defaultRange.endDate)]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSummary();
  }, []);

  const fetchSummary = () => {
    setLoading(true);
    const params = {};
    if (dateRange?.[0]) params.startDate = dateRange[0].format('YYYY-MM-DD');
    if (dateRange?.[1]) params.endDate = dateRange[1].format('YYYY-MM-DD');
    api.get('/revenue/summary', { params })
      .then((res) => setSummary(res.data))
      .catch(() => setSummary(null))
      .finally(() => setLoading(false));
  };

  const handlePreset = (preset) => {
    const range = getRange(preset);
    if (range) {
      setDateRange([dayjs(range.startDate), dayjs(range.endDate)]);
      setLoading(true);
      api.get('/revenue/summary', { params: range })
        .then((res) => setSummary(res.data))
        .catch(() => setSummary(null))
        .finally(() => setLoading(false));
    }
  };

  const handleExport = async () => {
    try {
      const params = {};
      if (dateRange?.[0]) params.startDate = dateRange[0].format('YYYY-MM-DD');
      if (dateRange?.[1]) params.endDate = dateRange[1].format('YYYY-MM-DD');
      const response = await api.get('/revenue/export', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'revenue-export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      message.success('Export downloaded');
    } catch (error) {
      message.error(error.response?.data?.message || 'Export failed');
    }
  };

  const formatMonth = (monthStr) => {
    if (!monthStr) return '';
    const [y, m] = monthStr.split('-');
    return dayjs().year(parseInt(y)).month(parseInt(m) - 1).format('MMMM YYYY');
  };

  const s = summary?.summary;
  const income = (s?.totalIncome ?? 0);
  const expenses = (s?.totalExpenses ?? 0);
  const profit = (s?.totalProfit ?? 0);

  return (
    <>
      <Card title={<span style={{ fontSize: 20, fontWeight: 600 }}>Revenue & Profit</span>}>
        <p style={{ color: '#666', marginBottom: 24 }}>
          Income from customer payments and revenue. Expenses from paid bills and employee payments. Profit = Income − Expenses.
        </p>

        <Card title="Date range" style={{ marginBottom: 24 }}>
          <Space wrap style={{ marginBottom: 12 }}>
            <Button onClick={() => handlePreset('today')}>Today</Button>
            <Button onClick={() => handlePreset('week')}>This week</Button>
            <Button onClick={() => handlePreset('month')}>This month</Button>
            <Button onClick={() => handlePreset('year')}>This year</Button>
          </Space>
          <Space wrap>
            <RangePicker value={dateRange} onChange={setDateRange} />
            <Button type="primary" onClick={fetchSummary} loading={loading}>Apply</Button>
            <Button onClick={handleExport}>Export CSV</Button>
          </Space>
        </Card>

        {summary && (
          <>
            <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={8}>
                <Card>
                  <Statistic title="Total Income" value={income} precision={2} prefix={<ArrowUpOutlined />} valueStyle={{ color: '#52c41a' }} suffix="$" />
                  <div style={{ fontSize: 12, color: '#666', marginTop: 8 }}>Customer payments & revenue</div>
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card>
                  <Statistic title="Total Expenses" value={expenses} precision={2} prefix={<ArrowDownOutlined />} valueStyle={{ color: '#ff4d4f' }} suffix="$" />
                  <div style={{ fontSize: 12, color: '#666', marginTop: 8 }}>Paid bills & employee payments</div>
                </Card>
              </Col>
              <Col xs={24} sm={8}>
                <Card>
                  <Statistic title="Total Profit" value={profit} precision={2} prefix={<DollarOutlined />} valueStyle={{ color: profit >= 0 ? '#52c41a' : '#ff4d4f' }} suffix="$" />
                  <div style={{ fontSize: 12, color: '#666', marginTop: 8 }}>Income − Expenses</div>
                </Card>
              </Col>
            </Row>

            <Card title="Daily breakdown" style={{ marginBottom: 24 }}>
              <Table
                rowKey={(_, i) => i}
                size="small"
                columns={[
                  { title: 'Date', dataIndex: 'date', key: 'date', render: (d) => dayjs(d).format('L') },
                  { title: 'Income', dataIndex: 'income', key: 'income', render: (v) => <span style={{ color: '#52c41a' }}>${(v || 0).toFixed(2)}</span> },
                  { title: 'Expenses', dataIndex: 'expenses', key: 'expenses', render: (v) => <span style={{ color: '#ff4d4f' }}>${(v || 0).toFixed(2)}</span> },
                  { title: 'Profit', dataIndex: 'profit', key: 'profit', render: (v) => <span style={{ color: (v || 0) >= 0 ? '#52c41a' : '#ff4d4f' }}>${(v || 0).toFixed(2)}</span> },
                ]}
                dataSource={summary.byDay ? [...summary.byDay].reverse() : []}
                pagination={false}
                locale={{ emptyText: 'No daily data for this range.' }}
                scroll={{ x: 'max-content' }}
              />
            </Card>

            <Card title="Month-end totals" style={{ marginBottom: 24 }}>
              <Table
                rowKey={(_, i) => i}
                size="small"
                columns={[
                  { title: 'Month', dataIndex: 'month', key: 'month', render: formatMonth },
                  { title: 'Income', dataIndex: 'income', key: 'income', render: (v) => <span style={{ color: '#52c41a' }}>${(v || 0).toFixed(2)}</span> },
                  { title: 'Expenses', dataIndex: 'expenses', key: 'expenses', render: (v) => <span style={{ color: '#ff4d4f' }}>${(v || 0).toFixed(2)}</span> },
                  { title: 'Profit', dataIndex: 'profit', key: 'profit', render: (v) => <span style={{ color: (v || 0) >= 0 ? '#52c41a' : '#ff4d4f' }}>${(v || 0).toFixed(2)}</span> },
                ]}
                dataSource={summary.byMonth ? [...summary.byMonth].reverse() : []}
                pagination={false}
                locale={{ emptyText: 'No monthly data.' }}
                scroll={{ x: 'max-content' }}
              />
            </Card>

            <Card title="Year-end totals" style={{ marginBottom: 24 }}>
              <Table
                rowKey={(_, i) => i}
                size="small"
                columns={[
                  { title: 'Year', dataIndex: 'year', key: 'year' },
                  { title: 'Income', dataIndex: 'income', key: 'income', render: (v) => <span style={{ color: '#52c41a' }}>${(v || 0).toFixed(2)}</span> },
                  { title: 'Expenses', dataIndex: 'expenses', key: 'expenses', render: (v) => <span style={{ color: '#ff4d4f' }}>${(v || 0).toFixed(2)}</span> },
                  { title: 'Profit', dataIndex: 'profit', key: 'profit', render: (v) => <span style={{ color: (v || 0) >= 0 ? '#52c41a' : '#ff4d4f' }}>${(v || 0).toFixed(2)}</span> },
                ]}
                dataSource={summary.byYear ? [...summary.byYear].reverse() : []}
                pagination={false}
                locale={{ emptyText: 'No yearly data.' }}
                scroll={{ x: 'max-content' }}
              />
            </Card>

            <Card title="By hostel" style={{ marginBottom: 24 }}>
              <Table
                rowKey={(_, i) => i}
                size="small"
                columns={[
                  { title: 'Hostel', dataIndex: 'hostel', key: 'hostel' },
                  { title: 'Income', dataIndex: 'income', key: 'income', render: (v) => <span style={{ color: '#52c41a' }}>${(v || 0).toFixed(2)}</span> },
                  { title: 'Expenses', dataIndex: 'expenses', key: 'expenses', render: (v) => <span style={{ color: '#ff4d4f' }}>${(v || 0).toFixed(2)}</span> },
                  { title: 'Profit', dataIndex: 'profit', key: 'profit', render: (v) => <span style={{ color: (v || 0) >= 0 ? '#52c41a' : '#ff4d4f' }}>${(v || 0).toFixed(2)}</span> },
                ]}
                dataSource={summary.breakdown || []}
                pagination={false}
                scroll={{ x: 'max-content' }}
              />
            </Card>

            <Card title="Recent income (revenue)" style={{ marginBottom: 24 }}>
              <Table
                rowKey="_id"
                size="small"
                columns={[
                  { title: 'Date', dataIndex: 'paymentDate', key: 'date', render: (d) => dayjs(d).format('L') },
                  { title: 'Hostel', dataIndex: ['hostel', 'name'], key: 'hostel', render: (t) => t || 'N/A' },
                  { title: 'Amount', dataIndex: 'amount', key: 'amount', render: (v) => `$${(v || 0).toFixed(2)}` },
                  { title: 'Method', dataIndex: 'paymentMethod', key: 'method', render: (t) => t || '-' },
                  { title: 'Description', dataIndex: 'description', key: 'desc', render: (t) => t || '-' },
                ]}
                dataSource={(summary.revenues || []).slice(0, 15)}
                pagination={false}
                scroll={{ x: 'max-content' }}
              />
            </Card>

            <Card title="Recent expenses (bills & employee payments)">
              <Table
                rowKey="key"
                size="small"
                columns={[
                  { title: 'Date', dataIndex: 'date', key: 'date', render: (d) => (d ? dayjs(d).format('L') : '-') },
                  { title: 'Hostel', dataIndex: 'hostel', key: 'hostel' },
                  { title: 'Type', dataIndex: 'type', key: 'type' },
                  { title: 'Vendor / Employee', dataIndex: 'label', key: 'label' },
                  { title: 'Amount', dataIndex: 'amount', key: 'amount', render: (v) => `$${(v || 0).toFixed(2)}` },
                ]}
                dataSource={[
                  ...(summary.expenses || []).map((b) => ({ key: `bill-${b._id}`, date: b.paymentDate, hostel: b.hostel?.name || 'Shared', type: 'Bill', label: b.vendorName || b.billType || '-', amount: b.amount || 0 })),
                  ...(summary.employeePayments || []).map((p, i) => ({ key: `emp-${i}`, date: p.paymentDate, hostel: p.hostel?.name || 'N/A', type: 'Employee payment', label: p.employee?.name || 'Employee', amount: p.amount || 0 })),
                ].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 20)}
                pagination={false}
                locale={{ emptyText: 'No recent expenses.' }}
                scroll={{ x: 'max-content' }}
              />
            </Card>
          </>
        )}
      </Card>
    </>
  );
};

export default RevenueSummary;
